// home/handLibrary/tournamentContexts.ts (ST-LIB) - the LOCAL-TOURNAMENT contexts the library generator
// stamps onto a `(gameType='tournament', pool=local)` hand.
//
// WHY THIS EXISTS. CX7's generator threads `gameType` + `pool` through ONE code path (generator.ts) and
// leaves the tournament block "representative" because the Phase 7 engine had not landed. ST-LIB fills that
// block with REAL tournament conditions: each context's `icmPressure` is engine-derived from a shipped DT
// payout structure via `riskProfileForStage` (strategy/icm.ts riskPremium -> icmPressureBucket) - the SAME
// ICM math ST1/ST2/ST-UI grade against - NOT a hand-picked label. That is what makes "the tournament engine
// feeding the same machine" (BUILD_PLAN §ST-LIB) literal: ST1's ICM computes the pressure that flavors the
// dealt hand, `liveRunner` threads it into the card-free digest, and the coach prompt (`ICM ${pressure}`)
// speaks the condition - so a baked tournament line reads like a tournament, at $0.
//
// HONEST COVERAGE SHAPE. The library bakes POSTFLOP narration (the coachCacheKey is a postflop FactsDigest).
// A real 14bb bubble stack is push/fold and rarely sees a flop, so the deepest ICM tax bites PREFLOP, which
// this postflop library does not narrate. The contexts therefore span the whole stack ladder - deep/mid
// (chip-EV, pressure 'none', where tournament postflop play looks like cash) through the bubble/ITM/final
// table (moderate/severe) at depths that still reach postflop. The coverage manifest makes that spread
// visible, exactly as CX7 requires the count to be deliberate.
//
// COST: $0. This module runs pure engine ICM math at import; no LLM, no network. The metered lane is only
// the (cost-gated) prose bake, same as cash.
import type { IcmPressure, TournamentPhase } from '@postflop/schema';
import { riskProfileForStage } from '@postflop/engine';
import { payoutStructureOf } from '../../engine/tournamentData';

/** A tournament library context: a real DT stage snapshot + the depth to deal it at + its engine-derived
 *  ICM pressure. `librarySpec` reads this to build a self-describing `spec.tournament` block. */
export interface LibraryTournamentContext {
  /** stable id for logs/manifests, e.g. `local_100:bubble`. */
  id: string;
  presetId: string;
  phase: TournamentPhase;
  /** the depth (BB) to deal the hand at - varied across contexts so the cell covers the stack ladder. */
  stackDepthBB: number;
  /** engine-derived (priceable stages) or 'none' for deep/chip-EV stages. */
  icmPressure: IcmPressure;
  /** the real ladder + field from the DT snapshot (so the block is honest + self-describing). */
  payouts: number[];
  stacks: number[];
  effectiveStackBB: number;
}

/** The raw plan: which (preset, stage) to seat, at what depth, and whether ICM is priced (a priceable
 *  stage snapshot) or chip-EV ('none' for deep/mid, which the DT snapshots' own notes call chip-EV). */
interface RawContext {
  presetId: string;
  phase: TournamentPhase;
  stackDepthBB: number;
  /** 'derive' ⇒ compute pressure from the stage snapshot via ST1's ICM; 'none' ⇒ chip-EV deep play. */
  pressure: 'derive' | 'none';
}

// A spread across BOTH shipped local tiers ($100 / $200), the whole stack ladder, and every pressure band.
// Deep/mid are chip-EV by the snapshots' own notes; bubble/itm/finalTable price ICM (stacks == playersLeft).
const RAW_CONTEXTS: readonly RawContext[] = [
  { presetId: 'local_100', phase: 'early', stackDepthBB: 80, pressure: 'none' },
  { presetId: 'local_100', phase: 'middle', stackDepthBB: 50, pressure: 'none' },
  { presetId: 'local_100', phase: 'itm', stackDepthBB: 35, pressure: 'derive' },
  { presetId: 'local_100', phase: 'finalTable', stackDepthBB: 30, pressure: 'derive' },
  { presetId: 'local_100', phase: 'bubble', stackDepthBB: 25, pressure: 'derive' },
  { presetId: 'local_200', phase: 'middle', stackDepthBB: 55, pressure: 'none' },
  { presetId: 'local_200', phase: 'itm', stackDepthBB: 32, pressure: 'derive' },
  { presetId: 'local_200', phase: 'bubble', stackDepthBB: 22, pressure: 'derive' },
];

/** Index of the covering chip leader (the max stack) - the villain an ICM all-in is priced against. */
const leaderIndex = (stacks: readonly number[]): number => {
  let best = 0;
  for (let i = 1; i < stacks.length; i++) if (stacks[i]! > stacks[best]!) best = i;
  return best;
};

/** Build the concrete contexts, deriving real ICM pressure for priceable stages. A stage that cannot be
 *  priced (throws in the ICM math - a sampled/degenerate snapshot) is SKIPPED, never faked, so the corpus
 *  only ever carries honest labels. Pure + deterministic (no RNG): the same corpus yields the same list. */
function buildContexts(): LibraryTournamentContext[] {
  const out: LibraryTournamentContext[] = [];
  for (const rc of RAW_CONTEXTS) {
    let structure;
    try {
      structure = payoutStructureOf(rc.presetId);
    } catch {
      continue; // preset absent from the corpus - skip, do not crash the generator.
    }
    const snap = structure.stages.find((s) => s.stage === rc.phase);
    if (!snap) continue;

    let pressure: IcmPressure = 'none';
    if (rc.pressure === 'derive') {
      const heroIndex = Math.min(Math.max((snap.heroStackRank ?? 1) - 1, 0), snap.stacks.length - 1);
      let villainIndex = leaderIndex(snap.stacks);
      if (villainIndex === heroIndex) villainIndex = heroIndex === 0 ? 1 : 0;
      try {
        pressure = riskProfileForStage(structure, rc.phase, heroIndex, villainIndex).pressure;
      } catch {
        // a snapshot the ICM math refuses to price (sample/fully-degenerate) - skip this context rather
        // than stamp a guessed pressure. The remaining contexts still give the cell a real spread.
        continue;
      }
    }

    out.push({
      id: `${rc.presetId}:${rc.phase}`,
      presetId: rc.presetId,
      phase: rc.phase,
      stackDepthBB: rc.stackDepthBB,
      icmPressure: pressure,
      payouts: [...snap.payoutsInPlay],
      stacks: [...snap.stacks],
      effectiveStackBB: rc.stackDepthBB,
    });
  }
  return out;
}

/** The engine-derived local-tournament contexts (computed once at import). The generator rotates hero
 *  through these by seed so the tournament cell covers the ladder + every pressure band the DT data prices. */
export const TOURNAMENT_CONTEXTS: readonly LibraryTournamentContext[] = buildContexts();
